/**
 * Manobra com confirmação explícita (só no CCO, ISA-101) — Nexora Grid (frontend, US-402).
 *
 * Criado em: 16/10/2026 · Última modificação: 17/10/2026 · Versão: 1.1.0
 *
 * O backend repassa ao motor, que faz o Lambda de pré-execução (relação, telemetria, intertravamentos, perfil e
 * janela de acesso) e espera a confirmação do atuador. A tela mostra o desfecho e cada checagem. Quem pede fica na
 * auditoria (tabela `comandos`). GUS-1105: com o modelo hidráulico do setor incompleto, mostra o aviso registrado.
 */
import { useState } from "react";
import { api, ErroApi } from "../api";

const ACOES: [string, string][] = [["DESLIGAR_BOMBA", "Desligar bomba"], ["LIGAR_BOMBA", "Ligar bomba"]];
type Resultado = { status: string; motivo: string; aviso_modelo?: string; checagens: { nome: string; aprovada: boolean; detalhe: string }[] };
const DESFECHOS: Record<string, string> = { confirmado: "Manobra confirmada pelo equipamento.", negado: "Manobra negada pelas checagens.",
  rejeitado: "O equipamento recusou a manobra.", sem_confirmacao: "Sem confirmação do equipamento no tempo limite.",
  falha_envio: "Falha ao enviar ao equipamento.", repetido: "Pedido repetido (ignorado)." };

export function Manobra({ empresa, device, nome }: { empresa: string; device: string; nome: string }) {
  const [acao, setAcao] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<Resultado | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const confirmar = async () => {
    setEnviando(true); setErro(null);
    try { setResultado(await api<Resultado>("/v1/manobras", { corpo: { empresa, atuador: device, acao, params: {} } })); }
    catch (e) { setErro(e instanceof ErroApi ? e.message : String(e)); }
    finally { setEnviando(false); setAcao(null); }
  };
  return (
    <div className="nx-cartao manobra">
      <h3>Manobra</h3>
      <div className="linha-botoes">
        {ACOES.map(([v, t]) => <button key={v} type="button" className="nx-botao nx-botao-manobra" onClick={() => { setAcao(v); setResultado(null); }}>
          {t}…</button>)}
      </div>
      {acao && (
        <div className="confirmacao" role="alertdialog" aria-labelledby="titulo-confirmacao" aria-describedby="texto-confirmacao">
          <strong id="titulo-confirmacao">Confirmar: {ACOES.find(([v]) => v === acao)?.[1]} — {nome}</strong>
          <p id="texto-confirmacao" className="nx-suave">O pedido passa pelas checagens (Lambda) antes de chegar ao equipamento e fica registrado com o seu login.</p>
          <div className="linha-botoes">
            <button type="button" className="nx-botao nx-botao-manobra" disabled={enviando} onClick={() => void confirmar()}>
              {enviando ? "Enviando…" : "Confirmar manobra"}</button>
            <button type="button" className="nx-botao" disabled={enviando} onClick={() => setAcao(null)}>Cancelar</button>
          </div>
        </div>
      )}
      {erro && <p className="erro" role="alert">{erro}</p>}
      {resultado && (
        <div className={`resultado-manobra status-${resultado.status}`} role="status">
          <b>{DESFECHOS[resultado.status] ?? resultado.status}</b>{resultado.motivo && <span className="nx-suave"> — {resultado.motivo}</span>}
          {resultado.aviso_modelo && <p className="aviso-modelo" role="alert">⚠ {resultado.aviso_modelo}</p>}
          <ul>{resultado.checagens.map((c) => <li key={c.nome}>{c.aprovada ? "✔" : "✘"} {c.nome}<span className="nx-suave"> · {c.detalhe}</span></li>)}</ul>
        </div>
      )}
    </div>
  );
}
